import {
  Badge,
  Box,
  Center,
  Group,
  Grid,
  keys,
  LoadingOverlay,
  Pagination,
  ScrollArea,
  Select,
  Table,
  Text,
  TextInput,
  UnstyledButton,
} from "@mantine/core";
import React, { useEffect, useMemo, useReducer, useState } from "react";
import { useParams } from "react-router-dom";
import { url } from "../../settings/linkUrl";
import {
  IconSelector,
  IconChevronDown,
  IconChevronUp,
  IconSearch,
} from "@tabler/icons-react";
import chrome from "../AdminPageChrome.module.css";

interface RowData {
  stdid: string;
  number_true: string;
  name: string;
  lname: string;
  vut: string;
  levelk: string;
  programe: string;
  yearrub: string;
  creck: string;
}

interface ThProps {
  children: React.ReactNode;
  reversed: boolean;
  sorted: boolean;
  onSort(): void;
}

interface StatusCounts {
  total: number;
  paid: number;
  pending: number;
  notRegistered: number;
}

interface FacultySummaryRow {
  name: string;
  total: number;
  paid: number;
  pending: number;
  notRegistered: number;
}

function countByStatus(rows: RowData[]): StatusCounts {
  let paid = 0;
  let pending = 0;
  let notRegistered = 0;

  for (const row of rows) {
    if (row.creck === "1") paid += 1;
    else if (row.creck === "0") pending += 1;
    else notRegistered += 1;
  }

  return {
    total: rows.length,
    paid,
    pending,
    notRegistered,
  };
}

function summarizeByFaculty(rows: RowData[]): FacultySummaryRow[] {
  const map = new Map<string, Omit<FacultySummaryRow, "name" | "total">>();

  for (const row of rows) {
    const name = row.levelk?.trim() || "—";
    const current = map.get(name) || { paid: 0, pending: 0, notRegistered: 0 };

    if (row.creck === "1") current.paid += 1;
    else if (row.creck === "0") current.pending += 1;
    else current.notRegistered += 1;

    map.set(name, current);
  }

  return Array.from(map.entries())
    .map(([name, counts]) => ({
      name,
      ...counts,
      total: counts.paid + counts.pending + counts.notRegistered,
    }))
    .sort((a, b) => b.total - a.total);
}

function pct(part: number, total: number) {
  if (!total) return "0";
  return ((part / total) * 100).toFixed(1);
}

export default function AdministratorListnamePage() {
  let { id } = useParams();

  useEffect(() => {
    LoadPage();
  }, [id]);

  const [search, setSearch] = useState("");

  const [dataList, setDataList] = useState<RowData[]>([]);
  const [sortedData, setSortedData] = useState<RowData[]>([]);

  const [sortBy, setSortBy] = useState<keyof RowData | null>(null);
  const [reverseSortDirection, setReverseSortDirection] = useState(false);

  const initialState = {
    loading: "",
    error: "",
    data: [],
  };

  function apiReducer(state: any, action: any) {
    switch (action.type) {
      case "DATA_FETCH_START":
        return { ...state, loading: "yes" };
      case "DATA_FETCH_SUCCESS":
        return { ...state, loading: "", data: action.payload };
      case "DATA_FETCH_FAILURE":
        return { ...state, loading: "", error: action.payload };
      default:
        return state;
    }
  }

  function sortData(
    data: RowData[],
    payload: { sortBy: keyof RowData | null; reversed: boolean; search: string }
  ) {
    const { sortBy } = payload;
    if (!sortBy) {
      return filterData(data, payload.search);
    }

    return filterData(
      [...data].sort((a, b) => {
        if (payload.reversed) {
          return b[sortBy].localeCompare(a[sortBy]);
        }
        return a[sortBy].localeCompare(b[sortBy]);
      }),
      payload.search
    );
  }

  function filterData(data: RowData[], search: string) {
    const query = search.toLowerCase().trim();
    return data.filter((item) =>
      keys(data[0]).some((key) => item[key].toLowerCase().includes(query))
    );
  }

  function Th({ children, reversed, sorted, onSort }: ThProps) {
    const Icon = sorted
      ? reversed
        ? IconChevronUp
        : IconChevronDown
      : IconSelector;
    return (
      <Table.Th>
        <UnstyledButton onClick={onSort}>
          <Group gap={4} wrap="nowrap">
            <Text fw={600} fz="sm">
              {children}
            </Text>
            <Center>
              <Icon size="0.9rem" stroke={1.5} />
            </Center>
          </Group>
        </UnstyledButton>
      </Table.Th>
    );
  }

  const [data, dispatch] = useReducer(apiReducer, initialState);

  const LoadPage = () => {
    dispatch({ type: "DATA_FETCH_START" });
    const token = localStorage.getItem("token");
    const requestOptions = {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    };
    fetch(`${url}Listname/Listname`, requestOptions)
      .then((respone) => {
        if (!respone.ok) throw Error(respone.statusText);
        return respone.json();
      })
      .then((json) => {
        dispatch({ type: "DATA_FETCH_SUCCESS", payload: json });
        setSortedData(json);
        setDataList(json);
      })
      .catch((error) => {
        dispatch({ type: "DATA_FETCH_FAILURE", payload: error.message });
      });
  };

  useEffect(() => {
    LoadPage();
  }, []);

  const setSorting = (field: keyof RowData) => {
    const reversed = field === sortBy ? !reverseSortDirection : false;
    setReverseSortDirection(reversed);
    setSortBy(field);
    setSortedData(sortData(sortedData, { sortBy: field, reversed, search }));
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.currentTarget;
    setSearch(value);
    setSortedData(
      sortData(dataList, {
        sortBy,
        reversed: reverseSortDirection,
        search: value,
      })
    );
  };

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setitemsPerPage] = useState(50);
  const indexOfLastUser = currentPage * itemsPerPage;
  const indexOfFirstUser = indexOfLastUser - itemsPerPage;
  const currentUsers = sortedData.slice(indexOfFirstUser, indexOfLastUser);

  const handlePageChange = (pageNumber: any) => {
    setCurrentPage(pageNumber);
  };

  const goToPrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const goToNextPage = () => {
    if (currentPage < Math.ceil(sortedData.length / itemsPerPage)) {
      setCurrentPage(currentPage + 1);
    }
  };

  const setitemsPerPageInt = (e: any) => {
    let i = parseInt(e.toString());
    setitemsPerPage(i);
    handlePageChange(1);
    setCurrentPage(1);
  };

  const startIndex = Math.max(currentPage - Math.floor(itemsPerPage / 2), 1);
  const endIndex = Math.min(
    startIndex + itemsPerPage - 1,
    Math.ceil(sortedData.length / itemsPerPage)
  );

  const isFiltered = search.trim().length > 0;
  const statsSource = isFiltered ? sortedData : dataList;
  const statusStats = useMemo(() => countByStatus(statsSource), [statsSource]);
  const facultyStats = useMemo(() => summarizeByFaculty(statsSource), [statsSource]);

  const statCards = [
    {
      label: "บัณฑิตทั้งหมด",
      value: statusStats.total,
      color: "var(--admin-navy)",
      hint: isFiltered
        ? `จาก ${dataList.length.toLocaleString("th-TH")} คนทั้งระบบ`
        : "รายชื่อในระบบทั้งหมด",
    },
    {
      label: "ชำระเงินแล้ว",
      value: statusStats.paid,
      color: "var(--admin-success)",
      hint: `${pct(statusStats.paid, statusStats.total)}% ของที่แสดง`,
    },
    {
      label: "อยู่ระหว่างชำระเงิน",
      value: statusStats.pending,
      color: "var(--admin-warn)",
      hint: `${pct(statusStats.pending, statusStats.total)}% ของที่แสดง`,
    },
    {
      label: "ยังไม่ลงทะเบียน",
      value: statusStats.notRegistered,
      color: "var(--admin-danger)",
      hint: `${pct(statusStats.notRegistered, statusStats.total)}% ของที่แสดง`,
    },
  ];

  const statusBadge = (creck: string) => {
    if (creck === "1") {
      return <Badge color="teal" variant="light">ลงทะเบียนเรียบร้อยแล้ว</Badge>;
    }
    if (creck === "0") {
      return <Badge color="orange" variant="light">อยู่ระหว่างการชำระเงิน</Badge>;
    }
    return <Badge color="red" variant="light">ยังไม่ลงทะเบียน</Badge>;
  };

  const rows = currentUsers.map((element: any, index) => (
    <Table.Tr key={element.stdid}>
      <Table.Td style={{ textAlign: "center" }} width={50}>
        {indexOfFirstUser + index + 1}
      </Table.Td>
      <Table.Td>{element.stdid}</Table.Td>
      <Table.Td>{element.number_true}</Table.Td>
      <Table.Td>
        {element.name} {element.lname}
      </Table.Td>
      <Table.Td>{element.vut}</Table.Td>
      <Table.Td>{element.levelk}</Table.Td>
      <Table.Td>{element.programe}</Table.Td>
      <Table.Td style={{ textAlign: "center" }} width={200}>
        <Group justify="center">{statusBadge(element.creck)}</Group>
      </Table.Td>
    </Table.Tr>
  ));

  return (
    <>
      <LoadingOverlay visible={!!data.loading} />
      <header className={chrome.pageHeader}>
        <h1 className={chrome.title}>รายชื่อบัณฑิต</h1>
        <p className={chrome.subtitle}>
          ค้นหา เรียงลำดับ และตรวจสอบสถานะการลงทะเบียน
        </p>
      </header>

      <Grid mb="md">
        {statCards.map((stat) => (
          <Grid.Col span={{ base: 6, sm: 3 }} key={stat.label}>
            <div className="admin-stat-panel">
              <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                {stat.label}
              </Text>
              <Text fw={700} size="xl" c={stat.color} lh={1.2}>
                {stat.value.toLocaleString("th-TH")}
              </Text>
              <Text size="xs" c="dimmed" mt={4}>
                {stat.hint}
              </Text>
            </div>
          </Grid.Col>
        ))}
      </Grid>

      {isFiltered ? (
        <Text size="sm" c="dimmed" mb="sm">
          สถิติด้านบนอิงจากผลการค้นหา ({sortedData.length.toLocaleString("th-TH")} รายการ)
        </Text>
      ) : null}

      <div className="admin-panel" style={{ marginBottom: "1rem" }}>
        <Group justify="space-between" mb="sm" wrap="wrap">
          <div>
            <Text fw={600} c="var(--admin-navy)">
              สรุปแยกตามคณะ
            </Text>
            <Text size="sm" c="dimmed" mt={4}>
              {isFiltered ? "จากผลการค้นหา" : "จากรายชื่อบัณฑิตทั้งหมดในระบบ"}
            </Text>
          </div>
          <Badge color="blue" variant="light">
            {facultyStats.length.toLocaleString("th-TH")} คณะ
          </Badge>
        </Group>
        <ScrollArea h={220} type="auto">
          <div className="admin-table-wrap">
            <Table highlightOnHover withRowBorders fz="sm">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th style={{ textAlign: "center", width: 48 }}>#</Table.Th>
                  <Table.Th>คณะ</Table.Th>
                  <Table.Th style={{ textAlign: "center" }}>ทั้งหมด</Table.Th>
                  <Table.Th style={{ textAlign: "center" }}>ชำระแล้ว</Table.Th>
                  <Table.Th style={{ textAlign: "center" }}>รอชำระ</Table.Th>
                  <Table.Th style={{ textAlign: "center" }}>ยังไม่ลงทะเบียน</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {facultyStats.length > 0 ? (
                  facultyStats.map((row, index) => (
                    <Table.Tr key={row.name}>
                      <Table.Td style={{ textAlign: "center" }}>{index + 1}</Table.Td>
                      <Table.Td>{row.name}</Table.Td>
                      <Table.Td style={{ textAlign: "center", fontWeight: 600 }}>
                        {row.total.toLocaleString("th-TH")}
                      </Table.Td>
                      <Table.Td style={{ textAlign: "center" }}>
                        <Badge color="teal" variant="light">
                          {row.paid.toLocaleString("th-TH")}
                        </Badge>
                      </Table.Td>
                      <Table.Td style={{ textAlign: "center" }}>
                        <Badge color="orange" variant="light">
                          {row.pending.toLocaleString("th-TH")}
                        </Badge>
                      </Table.Td>
                      <Table.Td style={{ textAlign: "center" }}>
                        <Badge color="red" variant="light">
                          {row.notRegistered.toLocaleString("th-TH")}
                        </Badge>
                      </Table.Td>
                    </Table.Tr>
                  ))
                ) : (
                  <Table.Tr>
                    <Table.Td colSpan={6}>
                      <div className="admin-empty">ยังไม่มีข้อมูลสถิติ</div>
                    </Table.Td>
                  </Table.Tr>
                )}
              </Table.Tbody>
            </Table>
          </div>
        </ScrollArea>
      </div>

      <div className="admin-toolbar">
        <Text size="sm" c="dimmed">
          ทั้งหมด {sortedData.length} รายการ
        </Text>
        <TextInput
          className={chrome.searchInput}
          placeholder="ค้นหาด้วยรหัส ชื่อ คณะ หรือสถานะ"
          leftSection={<IconSearch size="0.9rem" stroke={1.5} />}
          value={search}
          onChange={handleSearchChange}
        />
      </div>

      <div className="admin-table-wrap">
        <ScrollArea>
          <Table fz="sm" striped highlightOnHover withRowBorders>
            <Table.Thead>
              <Table.Tr>
                <Table.Th style={{ textAlign: "center" }}>#</Table.Th>
                <Th
                  sorted={sortBy === "stdid"}
                  reversed={reverseSortDirection}
                  onSort={() => setSorting("stdid")}
                >
                  รหัสประจำตัว
                </Th>
                <Th
                  sorted={sortBy === "number_true"}
                  reversed={reverseSortDirection}
                  onSort={() => setSorting("number_true")}
                >
                  ลำดับ
                </Th>
                <Th
                  sorted={sortBy === "name"}
                  reversed={reverseSortDirection}
                  onSort={() => setSorting("name")}
                >
                  ชื่อ - นามสกุล
                </Th>
                <Th
                  sorted={sortBy === "vut"}
                  reversed={reverseSortDirection}
                  onSort={() => setSorting("vut")}
                >
                  วุฒิ
                </Th>
                <Th
                  sorted={sortBy === "levelk"}
                  reversed={reverseSortDirection}
                  onSort={() => setSorting("levelk")}
                >
                  คณะ
                </Th>
                <Th
                  sorted={sortBy === "programe"}
                  reversed={reverseSortDirection}
                  onSort={() => setSorting("programe")}
                >
                  สาขาวิชา
                </Th>
                <Th
                  sorted={sortBy === "creck"}
                  reversed={reverseSortDirection}
                  onSort={() => setSorting("creck")}
                >
                  สถานะ
                </Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rows.length > 0 ? (
                rows
              ) : (
                <Table.Tr>
                  <Table.Td colSpan={8}>
                    <div className="admin-empty">ไม่พบข้อมูล</div>
                  </Table.Td>
                </Table.Tr>
              )}
            </Table.Tbody>
          </Table>
        </ScrollArea>
      </div>

      <Box className={chrome.footerBar}>
        <Select
          style={{ width: "110px" }}
          size="sm"
          label="แสดง"
          onChange={setitemsPerPageInt}
          value={itemsPerPage.toString()}
          data={[
            { value: "5", label: "5" },
            { value: "10", label: "10" },
            { value: "20", label: "20" },
            { value: `${Math.max(sortedData.length, 1)}`, label: "ทั้งหมด" },
          ]}
        />
        <Pagination
          withEdges
          total={endIndex}
          size="sm"
          color="blue"
          onNextPage={() => goToNextPage()}
          onPreviousPage={() => goToPrevPage()}
          onChange={handlePageChange}
        />
      </Box>
    </>
  );
}
